import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Shield, UserPlus, Trash2, Crown, Users, Briefcase, HeadphonesIcon, RefreshCw } from 'lucide-react';

type AppRole = 'admin' | 'manager' | 'support' | 'user';

interface UserRole {
  id: string;
  user_id: string;
  role: AppRole;
  created_at: string;
  email?: string;
}

interface UserProfile {
  id: string;
  email: string;
}

const ROLE_CONFIG: Record<AppRole, { label: string; description: string; icon: React.ReactNode; color: string }> = {
  admin: {
    label: 'Admin',
    description: 'Full access to all features, user management, and role assignment',
    icon: <Crown className="w-4 h-4" />,
    color: 'bg-purple-500/10 text-purple-600 border-purple-500/20',
  },
  manager: {
    label: 'Logistics Manager',
    description: 'Access to user approvals, package management, and reports',
    icon: <Briefcase className="w-4 h-4" />,
    color: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
  },
  support: {
    label: 'Support',
    description: 'View packages and assist customers, limited edit access',
    icon: <HeadphonesIcon className="w-4 h-4" />,
    color: 'bg-green-500/10 text-green-600 border-green-500/20',
  },
  user: {
    label: 'User',
    description: 'Standard user with access to their own data only',
    icon: <Users className="w-4 h-4" />,
    color: 'bg-gray-500/10 text-gray-600 border-gray-500/20',
  },
};

export const RoleManagement = () => {
  const [roles, setRoles] = useState<UserRole[]>([]);
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [selectedRole, setSelectedRole] = useState<AppRole>('support');
  const [searchEmail, setSearchEmail] = useState('');
  const [filterRole, setFilterRole] = useState<AppRole | 'all'>('all');

  useEffect(() => {
    fetchRoles();
    fetchUsers();
  }, []);

  const fetchRoles = async () => {
    const { data, error } = await supabase
      .from('user_roles')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      // Log only in development to prevent info leakage
      if (import.meta.env.DEV) {
        console.error('Error fetching roles:', error);
      }
      toast.error('Failed to load roles');
      return;
    }

    // Fetch emails for each role
    const rolesWithEmails = await Promise.all(
      (data || []).map(async (role) => {
        const { data: profile } = await supabase
          .from('profiles')
          .select('email')
          .eq('id', role.user_id)
          .single();
        return { ...role, email: profile?.email } as UserRole;
      })
    );

    setRoles(rolesWithEmails);
    setLoading(false);
  };

  const fetchUsers = async () => {
    const { data, error } = await supabase
      .from('profiles')
      .select('id, email')
      .order('email');

    if (error) {
      // Log only in development to prevent info leakage
      if (import.meta.env.DEV) {
        console.error('Error fetching users:', error);
      }
      return;
    }

    setUsers((data || []) as UserProfile[]);
  };

  const assignRole = async () => {
    if (!selectedUserId || !selectedRole) {
      toast.error('Please select a user and role');
      return;
    }

    // Check if user already has this role
    const existingRole = roles.find(
      (r) => r.user_id === selectedUserId && r.role === selectedRole
    );
    if (existingRole) {
      toast.error('User already has this role');
      return;
    }

    const { error } = await supabase.from('user_roles').insert({
      user_id: selectedUserId,
      role: selectedRole,
    });

    if (error) {
      // Log only in development to prevent info leakage
      if (import.meta.env.DEV) {
        console.error('Error assigning role:', error);
      }
      toast.error('Failed to assign role');
      return;
    }

    toast.success('Role assigned successfully');
    setIsDialogOpen(false);
    setSelectedUserId('');
    setSelectedRole('support');
    fetchRoles();
  };

  const removeRole = async (roleId: string, email: string, role: AppRole) => {
    // Prevent removing the last admin
    const adminRoles = roles.filter((r) => r.role === 'admin');
    if (role === 'admin' && adminRoles.length <= 1) {
      toast.error('Cannot remove the last admin role');
      return;
    }

    const { error } = await supabase.from('user_roles').delete().eq('id', roleId);

    if (error) {
      // Log only in development to prevent info leakage
      if (import.meta.env.DEV) {
        console.error('Error removing role:', error);
      }
      toast.error('Failed to remove role');
      return;
    }

    toast.success(`Removed ${ROLE_CONFIG[role].label} role from ${email}`);
    fetchRoles();
  };

  const changeRole = async (roleId: string, userId: string, currentRole: AppRole, newRole: AppRole, email: string) => {
    if (currentRole === newRole) return;

    // Prevent changing the last admin to another role
    const adminRoles = roles.filter((r) => r.role === 'admin');
    if (currentRole === 'admin' && adminRoles.length <= 1) {
      toast.error('Cannot change the last admin role');
      return;
    }

    // Check if user already has the new role
    const existingRole = roles.find(
      (r) => r.user_id === userId && r.role === newRole
    );
    if (existingRole) {
      toast.error('User already has this role');
      return;
    }

    const { error } = await supabase
      .from('user_roles')
      .update({ role: newRole })
      .eq('id', roleId);

    if (error) {
      // Log only in development to prevent info leakage
      if (import.meta.env.DEV) {
        console.error('Error changing role:', error);
      }
      toast.error('Failed to change role');
      return;
    }

    toast.success(`Changed ${email}'s role from ${ROLE_CONFIG[currentRole].label} to ${ROLE_CONFIG[newRole].label}`);
    fetchRoles();
  };

  const getRoleBadge = (role: AppRole) => {
    const config = ROLE_CONFIG[role];
    return (
      <Badge variant="outline" className={config.color}>
        {config.icon}
        <span className="ml-1">{config.label}</span>
      </Badge>
    );
  };

  // Filter users who don't already have the selected role
  const availableUsers = users.filter((user) => {
    const userHasRole = roles.some(
      (r) => r.user_id === user.id && r.role === selectedRole
    );
    const matchesSearch = user.email.toLowerCase().includes(searchEmail.toLowerCase());
    return !userHasRole && matchesSearch;
  });

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="text-center text-muted-foreground">Loading roles...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Role Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {(Object.entries(ROLE_CONFIG) as [AppRole, typeof ROLE_CONFIG[AppRole]][]).map(
          ([role, config]) => {
            const count = roles.filter((r) => r.role === role).length;
            const isActive = filterRole === role;
            return (
              <Card 
                key={role} 
                className={`relative overflow-hidden cursor-pointer transition-all hover:shadow-md ${
                  isActive ? 'ring-2 ring-primary ring-offset-2' : ''
                }`}
                onClick={() => setFilterRole(isActive ? 'all' : role)}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className={`p-2 rounded-full ${config.color}`}>
                        {config.icon}
                      </div>
                      <div>
                        <p className="font-semibold">{config.label}</p>
                        <p className="text-xs text-muted-foreground">
                          {count} {count === 1 ? 'user' : 'users'}
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          }
        )}
      </div>

      {/* Role Management Table */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5" />
              Team Roles & Permissions
            </CardTitle>
            <CardDescription>
              Manage team member access levels. Only admins can assign roles.
            </CardDescription>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <UserPlus className="w-4 h-4 mr-2" />
                Assign Role
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Assign Role to User</DialogTitle>
                <DialogDescription>
                  Select a user and the role you want to assign to them.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Role</Label>
                  <Select
                    value={selectedRole}
                    onValueChange={(value) => setSelectedRole(value as AppRole)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-background border-border">
                      {(Object.entries(ROLE_CONFIG) as [AppRole, typeof ROLE_CONFIG[AppRole]][]).map(
                        ([role, config]) => (
                          <SelectItem key={role} value={role}>
                            <div className="flex items-center gap-2">
                              {config.icon}
                              <span>{config.label}</span>
                            </div>
                          </SelectItem>
                        )
                      )}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    {ROLE_CONFIG[selectedRole].description}
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>Search User</Label>
                  <Input
                    placeholder="Search by email..."
                    value={searchEmail}
                    onChange={(e) => setSearchEmail(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Select User</Label>
                  <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a user..." />
                    </SelectTrigger>
                    <SelectContent className="bg-background border-border max-h-60">
                      {availableUsers.length === 0 ? (
                        <div className="p-2 text-sm text-muted-foreground text-center">
                          No users available
                        </div>
                      ) : (
                        availableUsers.map((user) => (
                          <SelectItem key={user.id} value={user.id}>
                            {user.email}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={assignRole} disabled={!selectedUserId}>
                  Assign Role
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent>
          {roles.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">
              No roles assigned yet. Use the button above to assign roles to team members.
            </p>
          ) : (
            <>
              {filterRole !== 'all' && (
                <div className="mb-4 flex items-center gap-2">
                  <Badge variant="secondary" className="text-sm">
                    Showing: {ROLE_CONFIG[filterRole].label}
                  </Badge>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => setFilterRole('all')}
                    className="h-6 px-2 text-xs"
                  >
                    Clear filter
                  </Button>
                </div>
              )}
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Assigned</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {roles
                    .filter((role) => filterRole === 'all' || role.role === filterRole)
                    .map((role) => (
                  <TableRow key={role.id}>
                    <TableCell className="font-medium">
                      {role.email || 'Unknown'}
                    </TableCell>
                    <TableCell>
                      <Select
                        value={role.role}
                        onValueChange={(value) =>
                          changeRole(role.id, role.user_id, role.role, value as AppRole, role.email || '')
                        }
                      >
                        <SelectTrigger className="w-36 h-8">
                          <SelectValue>
                            {getRoleBadge(role.role)}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent className="bg-background border-border">
                          {(Object.entries(ROLE_CONFIG) as [AppRole, typeof ROLE_CONFIG[AppRole]][]).map(
                            ([roleKey, config]) => (
                              <SelectItem key={roleKey} value={roleKey}>
                                <div className="flex items-center gap-2">
                                  {config.icon}
                                  <span>{config.label}</span>
                                </div>
                              </SelectItem>
                            )
                          )}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {new Date(role.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        onClick={() => removeRole(role.id, role.email || '', role.role)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}
        </CardContent>
      </Card>

      {/* Permissions Reference */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Role Permissions Reference</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {(Object.entries(ROLE_CONFIG) as [AppRole, typeof ROLE_CONFIG[AppRole]][]).map(
              ([role, config]) => (
                <div
                  key={role}
                  className="p-4 rounded-lg border bg-muted/30"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <div className={`p-1.5 rounded ${config.color}`}>
                      {config.icon}
                    </div>
                    <span className="font-semibold">{config.label}</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {config.description}
                  </p>
                </div>
              )
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
